import axios from 'axios';

/**
 * Candidate-actions API client.
 *
 * Centralises the small endpoints the candidate-detail toolbar uses:
 *  - Listing jobs that already contain a CV (for the badge + the
 *    "Perkelti CV" source dropdown)
 *  - Moving a CV between two jobs in the caller's company
 *  - Forwarding a CV summary by email to a colleague
 *
 * All methods unwrap the standard `{ message, data }` envelope and throw
 * with a useful message on non-2xx responses.
 */

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

function authHeaders(): Record<string, string> {
  const token = localStorage.getItem('auth_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export interface JobContainingCv {
  application_id: number;
  job_id: number;
  job_title: string;
  job_status: string;
  status: string;
  applied_at: string;
}

export interface MoveResult {
  message: string;
  result: 'moved' | 'merged';
}

export interface ForwardCvResponse {
  message: string;
  data: { recipient: string };
}

async function rethrow(err: any, fallback: string): Promise<never> {
  const apiMsg = err?.response?.data?.message || err?.message;
  const e = new Error(apiMsg || fallback);
  throw e;
}

export const candidateActionsApi = {
  listJobsForCv: async (cvId: number): Promise<JobContainingCv[]> => {
    try {
      const res = await axios.get(`${API_URL}/job-applications/cv/${cvId}/jobs`, {
        headers: authHeaders(),
      });
      return (res.data?.data || []) as JobContainingCv[];
    } catch (err) {
      return rethrow(err, 'Nepavyko gauti skelbimų sąrašo');
    }
  },

  moveCv: async (cvId: number, fromJobId: number, toJobId: number): Promise<MoveResult> => {
    try {
      const res = await axios.post(
        `${API_URL}/job-applications/move`,
        { cv_id: cvId, from_job_id: fromJobId, to_job_id: toJobId },
        { headers: authHeaders() },
      );
      return res.data as MoveResult;
    } catch (err) {
      return rethrow(err, 'Nepavyko perkelti kandidato');
    }
  },

  forwardByEmail: async (
    cvId: number,
    recipient: string,
    message?: string,
  ): Promise<ForwardCvResponse> => {
    try {
      const res = await axios.post(
        `${API_URL}/cv/${cvId}/forward-email`,
        { recipient, message: message || undefined },
        { headers: authHeaders() },
      );
      return res.data as ForwardCvResponse;
    } catch (err) {
      return rethrow(err, 'Nepavyko išsiųsti CV el. paštu');
    }
  },
};

export default candidateActionsApi;
